import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import defaults from './ui.defaults.json';
export type UiConfiguration = typeof defaults & {configurationRevision?:number};
const Context = createContext<UiConfiguration>(defaults);
export function DesignProvider({ children, theme }: { children: ReactNode; theme: string }) {
  const [configuration, setConfiguration] = useState<UiConfiguration>(defaults);
  useEffect(() => {
    let live = true;
    let revision = 0;
    const adopt = (next:UiConfiguration) => {
      if (!live || next.schemaVersion !== defaults.schemaVersion) return;
      const merged={...defaults,...next,tokens:{...defaults.tokens,...next.tokens},copy:{...defaults.copy,...next.copy},controls:{...defaults.controls,...next.controls},assets:{...defaults.assets,...next.assets}};
      setConfiguration(old => (next.configurationRevision??0)<(old.configurationRevision??0)||JSON.stringify(old)===JSON.stringify(merged)?old:merged);
    };
    const refresh = async () => {
      const requestRevision=++revision;
      try {
        const response = await fetch('/api/ui');
        if (response.ok && live && requestRevision===revision) {
          const next = await response.json() as UiConfiguration;
          if(requestRevision===revision)adopt(next);
        }
      } catch { /* The packaged design profile remains available offline. */ }
    };
    // Reuse the game's connection for rare design changes. No per-player polling.
    const changed=(event:Event)=>{const next=(event as CustomEvent<UiConfiguration>).detail;if(next){revision++;adopt(next);}else void refresh();};
    const visible=()=>{if(!document.hidden)void refresh();};
    window.addEventListener('tombala:ui-changed',changed);
    document.addEventListener('visibilitychange',visible);
    void refresh();
    return () => { live = false; window.removeEventListener('tombala:ui-changed',changed);document.removeEventListener('visibilitychange',visible); };
  }, []);
  useEffect(() => {
    const tokens = { ...configuration.tokens, ...(configuration.themes as Record<string, Record<string,string>>)[theme] };
    for (const [name, value] of Object.entries(tokens)) document.documentElement.style.setProperty(name, value);
  }, [configuration, theme]);
  return <Context.Provider value={configuration}>{children}</Context.Provider>;
}
export const useUi = () => useContext(Context);
// Local preview uses the real presentation context without polling or changing
// document tokens / the saved profile.
export function UiPreview({configuration,children}:{configuration:UiConfiguration;children:ReactNode}){return <Context.Provider value={configuration}>{children}</Context.Provider>;}
export function useCopy() {
  const ui = useUi();
  return (key: string, values: Record<string, string | number> = {}) => {
    const template = (ui.copy as Record<string,string>)[key] ?? key;
    return template.replace(/\{(\w+)\}/g, (match, name: string) => values[name] === undefined ? match : String(values[name]));
  };
}
